// The email builder's block catalogue: what each block is called, its palette icon, and what a fresh
// one looks like. Rendering lives in the shared renderer (lib/emailRender.js).
export const newId = () => (globalThis.crypto?.randomUUID ? crypto.randomUUID().slice(0, 12) : Math.random().toString(36).slice(2, 14));

const I = ({ d, children }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d ? <path d={d} /> : children}
  </svg>
);

export const BLOCKS = {
  heading: { label: 'Heading', group: 'Basics', icon: <I d="M6 4v16M18 4v16M6 12h12" />, make: () => ({ type: 'heading', html: 'Your headline', level: 1 }) },
  text: { label: 'Text', group: 'Basics', icon: <I d="M4 6h16M4 10h16M4 14h12M4 18h9" />, make: () => ({ type: 'text', html: '<p>Write something…</p>' }) },
  image: {
    label: 'Image',
    group: 'Basics',
    icon: (
      <I>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="9" cy="10" r="2" />
        <path d="m21 16-5-5-9 9" />
      </I>
    ),
    make: () => ({ type: 'image', url: '', alt: '', link: '', width: 100 }),
  },
  button: {
    label: 'Button',
    group: 'Basics',
    icon: (
      <I>
        <rect x="3" y="8" width="18" height="8" rx="4" />
        <path d="M9 12h6" />
      </I>
    ),
    make: () => ({ type: 'button', label: 'Place an order', url: '', size: 'md' }),
  },
  split: {
    label: 'Image + text',
    group: 'Layout',
    icon: (
      <I>
        <rect x="3" y="5" width="8" height="14" rx="1.5" />
        <path d="M14 7h7M14 11h7M14 15h5" />
      </I>
    ),
    make: () => ({ type: 'split', side: 'left', image: { url: '', alt: '', link: '' }, html: '<p><strong>A short headline</strong></p><p>A sentence or two about it.</p>', button: { label: 'Shop now', url: '' } }),
  },
  columns: {
    label: 'Columns',
    group: 'Layout',
    icon: (
      <I>
        <rect x="3" y="5" width="5" height="14" rx="1" />
        <rect x="9.5" y="5" width="5" height="14" rx="1" />
        <rect x="16" y="5" width="5" height="14" rx="1" />
      </I>
    ),
    make: () => ({
      type: 'columns',
      s: { align: 'center' },
      cols: [
        { image: '', title: 'Title', html: '<p>A line about it.</p>', label: 'Shop now', url: '' },
        { image: '', title: 'Title', html: '<p>A line about it.</p>', label: 'Shop now', url: '' },
      ],
    }),
  },
  spacer: { label: 'Spacer', group: 'Layout', icon: <I d="M12 4v4M12 16v4M8 8l4-4 4 4M8 16l4 4 4-4" />, make: () => ({ type: 'spacer', height: 24 }) },
  divider: { label: 'Divider', group: 'Layout', icon: <I d="M3 12h18" />, make: () => ({ type: 'divider' }) },
  products: {
    label: 'Products',
    group: 'Wholesale',
    icon: (
      <I>
        <path d="M6 7h12l-1 13H7L6 7z" />
        <path d="M9 7a3 3 0 0 1 6 0" />
      </I>
    ),
    make: () => ({ type: 'products', title: '', items: [], count: 4, perRow: 2, buttonLabel: '' }),
  },
  bestsellers: {
    label: 'Best sellers',
    group: 'Wholesale',
    icon: (
      <I>
        <path d="M12 3l2.7 5.5 6 .9-4.4 4.2 1 6L12 16.8 6.7 19.6l1-6L3.3 9.4l6-.9L12 3z" />
      </I>
    ),
    make: () => ({ type: 'bestsellers', title: 'Your best sellers — time to restock?', altTitle: 'Popular with stockists right now', count: 4, perRow: 2, showPrice: true, buttonLabel: 'Reorder', fallback: 'popular' }),
  },
  countdown: {
    label: 'Countdown',
    group: 'Wholesale',
    icon: (
      <I>
        <circle cx="12" cy="13" r="8" />
        <path d="M12 9v4l2.5 2.5M9 2h6" />
      </I>
    ),
    make: () => ({ type: 'countdown', source: 'date', endsAt: new Date(Date.now() + 7 * 864e5).toISOString(), heading: 'Offer ends in' }),
  },
  promotion: { label: 'Promotion', group: 'Wholesale', icon: <I d="M20 12 12 20l-9-9V3h8l9 9zM7.5 7.5h.01" />, make: () => ({ type: 'promotion' }) },
  lookbook: {
    label: 'Lookbook PDF',
    group: 'Wholesale',
    icon: (
      <I>
        <path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2V5z" />
        <path d="M8 7h6" />
      </I>
    ),
    make: () => ({ type: 'lookbook', label: 'View the lookbook (PDF)', selection: null, headline: '', intro: '' }),
  },
  video: {
    label: 'Video',
    group: 'Media',
    icon: (
      <I>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m10 9 5 3-5 3V9z" />
      </I>
    ),
    make: () => ({ type: 'video', url: '', thumbnail: '', label: 'Watch the video' }),
  },
  quote: { label: 'Quote', group: 'Media', icon: <I d="M7 7h4v6H7l-1 4M15 7h4v6h-4l-1 4" />, make: () => ({ type: 'quote', html: '<p>“A great quote from a happy stockist.”</p>', author: '' }) },
  social: {
    label: 'Social links',
    group: 'Media',
    icon: (
      <I>
        <circle cx="6" cy="12" r="2.5" />
        <circle cx="18" cy="6" r="2.5" />
        <circle cx="18" cy="18" r="2.5" />
        <path d="m8.2 10.8 7.6-3.6M8.2 13.2l7.6 3.6" />
      </I>
    ),
    make: () => ({
      type: 'social',
      s: { align: 'center' },
      links: [
        { network: 'instagram', url: 'https://www.instagram.com/teamm8/' },
        { network: 'website', url: 'https://teamm8.com' },
      ],
    }),
  },
};
export const GROUPS = ['Basics', 'Layout', 'Wholesale', 'Media'];
export const RICH_INLINE = new Set(['heading', 'text', 'quote']); // edited directly on the canvas

export const makeBlock = (type) => ({ ...BLOCKS[type].make(), id: newId() });
/** Deep copy with fresh id (duplicate). */
export const cloneBlock = (b) => ({ ...JSON.parse(JSON.stringify(b)), id: newId() });

/** What an empty block asks for on the canvas. */
export const PLACEHOLDER = {
  heading: 'Click to write a heading',
  text: 'Click to write',
  image: 'Add an image',
  button: 'Button',
  split: 'Add an image and text',
  columns: 'Add column content',
  products: 'Pick products or a collection',
  bestsellers: 'Each client’s own best sellers — this preview client has no order history or catalogue yet',
  countdown: 'Pick when the countdown ends',
  promotion: 'Shows the promotion — link this email to a promotion to use it',
  lookbook: 'Lookbook button — appears once a collection is chosen',
  video: 'Add a video link and thumbnail',
  quote: 'Click to write a quote',
  social: 'Add social links',
};
